import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import AdminShell from "@/components/AdminShell";
import { Eyebrow } from "@/components/brand";
import {
  Users, MessageSquare, CheckCircle2, Flag, FileText, BookOpen, CalendarClock,
  TrendingUp, TrendingDown, Minus, Send, X, Repeat, AlertCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Stat = ({ icon: Icon, label, value, sub, color = "#0e1b2a", testid }) => (
  <div className="card-flat p-5" data-testid={testid}>
    <div className="flex items-center gap-2 text-mute mb-2">
      <Icon className="w-3.5 h-3.5" />
      <span className="eyebrow">{label}</span>
    </div>
    <p className="text-3xl font-extrabold tracking-tight tabular-nums" style={{ color }}>{value}</p>
    {sub && <p className="text-mute text-xs mt-1">{sub}</p>}
  </div>
);

const TrendBadge = ({ trend }) => {
  if (trend === "up") return <span className="inline-flex items-center gap-0.5 text-equal-dark text-xs font-medium"><TrendingUp className="w-3.5 h-3.5" /> up</span>;
  if (trend === "down") return <span className="inline-flex items-center gap-0.5 text-[#c0392b] text-xs font-medium"><TrendingDown className="w-3.5 h-3.5" /> down</span>;
  return <span className="inline-flex items-center gap-0.5 text-mute text-xs font-medium"><Minus className="w-3.5 h-3.5" /> flat</span>;
};

const Sparkline = ({ series, max }) => (
  <div className="flex items-end gap-[3px] h-8" title={series.join(" · ")}>
    {series.map((v, i) => (
      <div key={i} className="w-2 rounded-sm bg-mint relative" style={{ height: "100%" }}>
        <div className="absolute bottom-0 left-0 right-0 rounded-sm bg-equal transition-all"
          style={{ height: `${Math.max(6, Math.round((v / max) * 100))}%` }} />
      </div>
    ))}
  </div>
);

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

export default function MonthlyReportPage() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const h = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const [report, setReport] = useState(null);
  const [patterns, setPatterns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState(""); // "year-month"
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);

  const fetchReport = useCallback(async (year, month) => {
    setLoading(true);
    try {
      const q = year && month ? `?year=${year}&month=${month}` : "";
      const [r, p] = await Promise.all([
        axios.get(`${API}/admin/monthly-report${q}`, h),
        axios.get(`${API}/admin/report-patterns?months=6`, h),
      ]);
      setReport(r.data);
      setPatterns(p.data);
      setSel(`${r.data.year}-${r.data.month}`);
    } catch {
      toast.error("Failed to load the monthly report");
    } finally { setLoading(false); }
  }, [h]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const onSelectMonth = (val) => {
    setSel(val);
    const [y, m] = val.split("-").map(Number);
    fetchReport(y, m);
  };

  const doSend = async () => {
    setSending(true);
    try {
      const [y, m] = sel.split("-").map(Number);
      const res = await axios.post(`${API}/admin/send-monthly-report`, { year: y, month: m }, h);
      toast.success(res.data.message || "Report sent");
      setConfirming(false);
    } catch {
      toast.error("Couldn't send the report");
    } finally { setSending(false); }
  };

  const monthLabel = report ? new Date(report.year, report.month - 1, 1).toLocaleString("default", { month: "long", year: "numeric" }) : "";
  const topicMax = useMemo(() => {
    if (!patterns) return 1;
    return Math.max(1, ...patterns.recurring_topics.flatMap((t) => t.series));
  }, [patterns]);
  const thisMonthTopicMax = report && report.top_topics.length ? Math.max(...report.top_topics.map((t) => t[1])) : 1;

  return (
    <AdminShell active="report">
      <div className="flex items-center gap-3 mb-6">
        <button onClick={() => navigate(-1)} className="btn-quiet btn-sm -ml-3" data-testid="report-back-button">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
          Back
        </button>
      </div>

      <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
        <div>
          <Eyebrow className="mb-3">Program Snapshot</Eyebrow>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-ink tracking-tight leading-[1.05]">Monthly Report</h1>
          <p className="text-mute mt-4 max-w-xl">A shareable snapshot of program activity and the subjects applicants keep raising — with recurring patterns for the City.</p>
        </div>
        <div className="flex items-center gap-2">
          {report && report.available_months.length > 0 && (
            <select value={sel} onChange={(e) => onSelectMonth(e.target.value)} data-testid="report-month-select"
              className="input-flat text-sm py-2 pr-8">
              {report.available_months.map((mo) => (
                <option key={`${mo.year}-${mo.month}`} value={`${mo.year}-${mo.month}`}>{mo.label}</option>
              ))}
            </select>
          )}
          {report && report.can_send && (
            <button onClick={() => setConfirming(true)} className="btn-primary btn-sm" data-testid="report-send-btn">
              <Send className="w-3.5 h-3.5" /> Approve &amp; Send
            </button>
          )}
        </div>
      </div>

      {loading || !report ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-line border-t-equal rounded-full animate-spin" />
        </div>
      ) : (
        <div className="max-w-5xl space-y-10">
          {!report.can_send && (
            <div className="flex items-center gap-2 text-mute text-xs -mt-4" data-testid="report-readonly-note">
              <AlertCircle className="w-3.5 h-3.5" /> Read-only view. Only an admin can send this report.
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <Stat icon={Users} label="Active applicants" value={report.active_applicants ?? 0} sub={`across ${report.consulting_sessions} sessions`} color="#0f8a5a" testid="report-stat-users" />
            <Stat icon={MessageSquare} label="Conversations" value={report.total_convs} sub={`${report.total_messages} messages`} testid="report-stat-convs" />
            <Stat icon={CheckCircle2} label="Sessions completed" value={report.completed_sessions} testid="report-stat-completed" />
            <Stat icon={Flag} label="Flagged for a human" value={report.flagged_count} color={report.flagged_count ? "#c0392b" : "#0e1b2a"} testid="report-stat-flagged" />
            <Stat icon={FileText} label="New knowledge" value={report.new_docs.length} sub="added this month" testid="report-stat-newdocs" />
            <Stat icon={CalendarClock} label="Consulting sessions" value={report.consulting_sessions} testid="report-stat-sessions" />
            <Stat icon={BookOpen} label="Knowledge base" value={report.total_docs} sub="total documents" testid="report-stat-totaldocs" />
          </div>

          {/* Recurring patterns — the highlight (only once 2+ months of data exist) */}
          {patterns && patterns.months_covered >= 2 && (
          <div data-testid="recurring-patterns">
            <div className="flex items-center gap-2 mb-1">
              <Repeat className="w-4 h-4 text-equal-dark" />
              <Eyebrow>Recurring patterns {patterns && `· last ${patterns.months_covered} month${patterns.months_covered === 1 ? "" : "s"}`}</Eyebrow>
            </div>
            <p className="text-mute text-sm mb-5 max-w-xl">Subjects and unmet needs that keep coming up across months — what the City should pay attention to.</p>

            <div className="grid md:grid-cols-2 gap-4">
              {/* Recurring subjects */}
              <div className="card-flat p-6">
                <h3 className="text-sm font-bold text-ink mb-4">Recurring subjects</h3>
                {patterns && patterns.recurring_topics.length > 0 ? (
                  <div className="space-y-4">
                    {patterns.recurring_topics.slice(0, 6).map((t) => (
                      <div key={t.topic} className="flex items-center gap-4" data-testid={`recurring-topic-${slug(t.topic)}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-ink truncate">{t.topic}</span>
                            <TrendBadge trend={t.trend} />
                          </div>
                          <p className="text-mute text-xs mt-0.5">
                            Seen in {t.months_present}/{patterns.months_covered} month{patterns.months_covered === 1 ? "" : "s"} · {t.total} questions total
                          </p>
                        </div>
                        <Sparkline series={t.series} max={topicMax} />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-mute text-sm">No recurring subjects yet — patterns build as months accumulate.</p>
                )}
              </div>

              {/* Recurring unmet needs */}
              <div className="card-flat p-6">
                <h3 className="text-sm font-bold text-ink mb-4">Recurring unmet needs</h3>
                {patterns && patterns.recurring_gaps.length > 0 ? (
                  <div className="space-y-3">
                    {patterns.recurring_gaps.map((g) => (
                      <div key={g.subject} className="border-l-2 border-warm pl-3" data-testid={`recurring-gap-${slug(g.subject)}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-ink">{g.subject}</span>
                          <span className="text-xs text-[#c47a12] bg-warm border border-[#f3d9b0] rounded-md px-1.5 py-0.5">
                            {g.months_present}/{patterns.months_covered} mo
                          </span>
                        </div>
                        {g.samples[0] && <p className="text-mute text-xs mt-1 line-clamp-2 italic">“{g.samples[0]}”</p>}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-mute text-sm">No recurring gaps — the AI is answering from the knowledge base.</p>
                )}
              </div>
            </div>
          </div>
          )}

          {/* This month's topics + gaps */}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="card-flat p-6" data-testid="report-topics">
              <Eyebrow className="mb-4">Top subjects this month</Eyebrow>
              {report.top_topics.length ? (
                <div className="space-y-2.5">
                  {report.top_topics.map(([topic, count]) => (
                    <div key={topic} className="flex items-center gap-4">
                      <span className="text-sm text-ink-2 w-40 flex-shrink-0 truncate">{topic}</span>
                      <div className="flex-1 h-2 bg-page rounded-full overflow-hidden border border-line">
                        <div className="h-full rounded-full bg-equal" style={{ width: `${Math.round((count / thisMonthTopicMax) * 100)}%` }} />
                      </div>
                      <span className="text-sm font-semibold text-ink w-8 text-right tabular-nums font-mono">{count}</span>
                    </div>
                  ))}
                </div>
              ) : <p className="text-mute text-sm">No questions this month yet.</p>}
            </div>

            <div className="card-flat p-6" data-testid="report-gaps">
              <Eyebrow className="mb-4">Unanswered this month</Eyebrow>
              {report.knowledge_gaps.length ? (
                <ul className="space-y-2.5">
                  {report.knowledge_gaps.map((g, i) => (
                    <li key={i} className="text-sm text-ink-2 pl-3 border-l-2 border-mint line-clamp-2">{g}</li>
                  ))}
                </ul>
              ) : <p className="text-mute text-sm">Nothing went unanswered this month.</p>}
            </div>
          </div>

          {/* New knowledge added */}
          <div className="card-flat p-6" data-testid="report-new-docs">
            <Eyebrow className="mb-4">Knowledge added in {monthLabel}</Eyebrow>
            {report.new_docs.length ? (
              <div className="space-y-4">
                {report.new_docs.map((d, i) => (
                  <div key={i} className="border-b border-line/60 last:border-0 pb-4 last:pb-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-ink">{d.filename}</span>
                      <span className="text-[10px] font-mono text-mute border border-line rounded px-1.5 py-0.5">{d.file_type}</span>
                      <span className="text-xs text-mute ml-auto">{d.uploaded_at}</span>
                    </div>
                    {(d.summary || d.description) && (
                      <p className="text-ink-2 text-sm mt-1.5 leading-relaxed">{d.summary || d.description}</p>
                    )}
                    {d.knowledge_points && d.knowledge_points.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {d.knowledge_points.slice(0, 4).map((kp, k) => (
                          <li key={k} className="text-mute text-xs flex gap-2"><span className="text-equal mt-0.5">•</span><span>{kp}</span></li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            ) : <p className="text-mute text-sm">No new documents were added this month.</p>}
          </div>
        </div>
      )}

      {/* Approve & Send confirmation */}
      {confirming && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink/40 backdrop-blur-sm px-4" data-testid="report-send-modal">
          <div className="bg-white rounded-2xl border border-line max-w-md w-full p-6 animate-rise-in">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="w-9 h-9 rounded-[10px] bg-mint grid place-items-center"><Send className="w-4 h-4 text-equal-dark" /></span>
                <h3 className="text-lg font-bold text-ink tracking-tight">Send this report?</h3>
              </div>
              <button onClick={() => setConfirming(false)} className="text-mute hover:text-ink" data-testid="report-send-close"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-ink-2 text-sm mt-4 leading-relaxed">
              This emails the <b>{monthLabel}</b> report to <span className="font-mono">yan@tkachventures.com</span>. Nothing is sent until you confirm — the report never sends automatically.
            </p>
            <div className="mt-6 flex items-center justify-end gap-2">
              <button onClick={() => setConfirming(false)} className="btn-ghost btn-sm" data-testid="report-send-cancel">Cancel</button>
              <button onClick={doSend} disabled={sending} className="btn-primary btn-sm" data-testid="report-send-confirm">
                {sending ? "Sending…" : "Approve & send"}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminShell>
  );
}
